import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Link } from "react-router-dom";
import { formatINR, useCart } from "../context/CartContext";
import { Star, X } from "lucide-react";
import { toast } from "sonner";
import SafeImg from "./SafeImg";

export default function QuickViewDialog({ product, open, onOpenChange }) {
  const { addItem } = useCart();
  const [size, setSize] = useState("M");

  if (!product) return null;

  const handleAdd = () => {
    addItem(product, { size, quantity: 1 });
    toast.success(`${product.name} added to cart`, {
      description: `Size ${size} · ${formatINR(product.price)}`,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="quickview-dialog"
        className="max-w-3xl bg-cream-50 border-blush-100 rounded-3xl p-0 overflow-hidden h-[92vh] sm:h-auto max-h-[92vh] sm:max-h-[90vh] overflow-y-auto"
      >
        {/* Visible close button (overrides the default tiny one) */}
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label="Close quick view"
          data-testid="quickview-close"
          className="absolute right-4 top-4 z-10 h-10 w-10 rounded-full bg-white/95 backdrop-blur border border-blush-100 grid place-items-center text-ink-700 hover:text-blush-600 hover:bg-blush-50 transition shadow-sm"
        >
          <X size={18} strokeWidth={1.8} />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="relative aspect-[4/5] md:aspect-auto bg-blush-50">
            <SafeImg
              src={product.images[0]}
              alt={product.name}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="p-6 sm:p-8 flex flex-col">
            <DialogHeader className="space-y-2 text-left pr-12">
              <DialogTitle className="font-heading text-2xl sm:text-3xl text-ink-900">
                {product.name}
              </DialogTitle>
              <DialogDescription className="font-body text-ink-500">
                {product.fabric}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-3 flex items-center gap-2 text-blush-600">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  size={14}
                  strokeWidth={1.5}
                  fill={i < Math.round(product.rating) ? "currentColor" : "none"}
                />
              ))}
              <span className="text-xs font-body text-ink-500 ml-1">
                {product.rating} · {product.reviews} reviews
              </span>
            </div>

            <div className="mt-4 flex items-baseline gap-3">
              <span className="font-heading text-2xl text-ink-900">
                {formatINR(product.price)}
              </span>
              {product.originalPrice && (
                <span className="font-body text-sm text-ink-500 line-through">
                  {formatINR(product.originalPrice)}
                </span>
              )}
            </div>

            <div className="mt-5">
              <p className="text-xs uppercase tracking-[0.22em] text-ink-500 font-body mb-2">
                Select size
              </p>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`quickview-size-${s}`}
                    onClick={() => setSize(s)}
                    className={`h-10 w-10 rounded-full border text-xs font-body transition ${
                      size === s
                        ? "bg-blush-600 text-white border-blush-600"
                        : "bg-white text-ink-700 border-blush-100 hover:border-blush-300"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-auto pt-6 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="quickview-add-btn"
                onClick={handleAdd}
                className="btn-maroon flex-1"
              >
                Add to cart
              </button>
              <Link
                to={`/product/${product.id}`}
                data-testid="quickview-details-link"
                className="btn-ghost-rose flex-1 text-center"
                onClick={() => onOpenChange(false)}
              >
                View details
              </Link>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
